import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconAdjustments, IconHistory, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { useBranches } from '../branches/branchesApi';
import { AdjustStockModal } from './AdjustStockModal';
import { MovementHistoryDrawer } from './MovementHistoryDrawer';
import { useStocks } from './inventoryApi';
import type { ItemStockDto } from './types';

export function InventoryPage() {
  const { t } = useTranslation();
  const { data: branches } = useBranches();

  const [branchId, setBranchId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [adjustTarget, setAdjustTarget] = useState<ItemStockDto | null>(null);
  const [historyTarget, setHistoryTarget] = useState<ItemStockDto | null>(null);

  // Default to the first branch once they load.
  const activeBranchId = useMemo(() => {
    if (branchId) return branchId;
    return branches && branches.length > 0 ? branches[0]!.branchId : null;
  }, [branchId, branches]);

  const { data: stocks, isLoading, error } = useStocks(activeBranchId);

  const filtered = useMemo(() => {
    if (!stocks) return [];
    const q = search.trim().toLowerCase();
    if (!q) return stocks;
    return stocks.filter(
      (s) =>
        s.itemNameAr.toLowerCase().includes(q) ||
        s.itemNameEn?.toLowerCase().includes(q) ||
        s.itemSku.toLowerCase().includes(q),
    );
  }, [stocks, search]);

  return (
    <Stack gap="md">
      <PageHeader title={t('nav.items.inventory')} />

      <Paper p="md" withBorder>
        <Group align="end" gap="sm" wrap="wrap">
          <Select
            label={t('inventory.branch')}
            data={
              branches?.map((b) => ({ value: b.branchId, label: b.nameAr })) ?? []
            }
            value={activeBranchId}
            onChange={setBranchId}
            w={260}
          />
          <TextInput
            label={t('inventory.search')}
            leftSection={<IconSearch size={16} stroke={1.6} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder={t('inventory.searchPlaceholder') ?? ''}
            w={280}
          />
        </Group>
      </Paper>

      {isLoading && (
        <Group justify="center" py="xl"><Loader size="sm" /></Group>
      )}
      {error && (
        <Alert color="red">
          {error instanceof Error ? error.message : t('inventory.errors.loadFailed')}
        </Alert>
      )}
      {stocks && stocks.length === 0 && !isLoading && (
        <Alert color="gray">{t('inventory.empty')}</Alert>
      )}
      {filtered.length > 0 && (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('inventory.columns.sku')}</Table.Th>
                <Table.Th>{t('inventory.columns.item')}</Table.Th>
                <Table.Th className="text-end">{t('inventory.columns.onHand')}</Table.Th>
                <Table.Th className="text-end">{t('inventory.columns.updated')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((s) => (
                <Table.Tr key={s.itemStockId}>
                  <Table.Td className="font-mono">{s.itemSku}</Table.Td>
                  <Table.Td>{s.itemNameAr}</Table.Td>
                  <Table.Td
                    className={`font-numeric text-end ${s.onHand < 0 ? 'text-red-600 font-bold' : ''}`}
                  >
                    {s.onHand}
                  </Table.Td>
                  <Table.Td className="font-numeric text-end">
                    {formatRelativeUpdated(s.updatedAt)}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end">
                      <Tooltip label={t('inventory.actions.history')}>
                        <ActionIcon variant="subtle" onClick={() => setHistoryTarget(s)}>
                          <IconHistory size={18} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('inventory.actions.adjust')}>
                        <ActionIcon variant="subtle" onClick={() => setAdjustTarget(s)}>
                          <IconAdjustments size={18} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <AdjustStockModal
        stock={adjustTarget}
        opened={adjustTarget !== null}
        onClose={() => setAdjustTarget(null)}
      />
      <MovementHistoryDrawer
        stock={historyTarget}
        opened={historyTarget !== null}
        onClose={() => setHistoryTarget(null)}
      />
    </Stack>
  );
}

function formatRelativeUpdated(iso: string): string {
  const date = new Date(iso);
  const secs = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (secs < 60) return 'الآن';
  if (secs < 3_600) return `${Math.floor(secs / 60)}د`;
  if (secs < 86_400) return `${Math.floor(secs / 3_600)}س`;
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);
}
